import { QueryResult, useQuery } from '@apollo/client';
import GET_USAGE_VERSION_COMPARE from '../api/getUsageVersionCompare';
import type { UsageVersionCompareData, UsageVersionCompareVariables } from '../types';

/**
 * RITA-1523 (Nutzungserfassung, Versionsvergleich) — die Kennzahlen zweier Stände.
 *
 * CONTRACT: `useQuery` mit `skip` und NICHT `useLazyQuery`. Die NutzungPage lädt Daten
 * ausschließlich über `useQuery` + `gql` (Vertrag der Titel-Matrix, die das gesamte Modul
 * '@apollo/client' durch einen dauerhaften Ladezustand ersetzt). `skip` hält die Anfrage zurück,
 * solange keine ZWEI VERSCHIEDENEN Stände gewählt sind: der Server lehnt eine leere oder doppelte
 * Kennung hart ab (usageRangeArgs.ts `assertAppVersion`, A ≠ B), und eine abgelehnte Anfrage wäre
 * eine Audit-Zeile ohne Ergebnis.
 *
 * Sonst dieselben drei Optionen wie `useUsageInsights` (Begründung dort): 'network-only',
 * errorPolicy 'none' (alle Wurzelfelder sind non-null — ein werfender Resolver liefert data: null,
 * halbe Zahlen wären schlimmer als ein Fehlerzustand) und notifyOnNetworkStatusChange, damit
 * „Erneut versuchen" und jede Auswahländerung einen Ladezustand zeigen.
 *
 * Jede Auswahländerung ist damit bewusst eine eigene Anfrage und eine weitere Audit-Zeile:
 * `usageVersionCompare` steht in apps/backend/src/services/audit/sensitiveReadWhitelist.ts, das
 * Operation-Log-Plugin schreibt also genau eine Zeile je Anfrage — ohne die gewählten Kennungen.
 * Der Absatz in docs/AUDIT-COVERAGE.md wird im Backend-Nachzug fortgeschrieben.
 */
export const useUsageVersionCompare = (
  days: number,
  versionA: string | null,
  versionB: string | null,
): QueryResult<UsageVersionCompareData, UsageVersionCompareVariables> =>
  useQuery<UsageVersionCompareData, UsageVersionCompareVariables>(GET_USAGE_VERSION_COMPARE, {
    // Die Variablen sind auch im übersprungenen Fall wohlgeformt: Apollo baut den Cache-Schlüssel
    // aus ihnen, bevor `skip` greift. Gefragt wird mit leerer Kennung nie.
    variables: { range: { days }, versionA: versionA ?? '', versionB: versionB ?? '' },
    skip: versionA === null || versionB === null || versionA === versionB,
    fetchPolicy: 'network-only',
    errorPolicy: 'none',
    notifyOnNetworkStatusChange: true,
  });

export default useUsageVersionCompare;
